import React, { useEffect, useState } from "react";
import {
  DEFAULT_EMAIL_SETTINGS,
  ensureSpreadsheet,
  readEmailSettings,
  spreadsheetUrl,
  writeEmailSettings,
} from "../api/sheets";
import { EmailSettings } from "../types/sheets";
import PersistentTextarea from "../components/PersistentTextarea";
import { useViewWidth } from "../components/ViewWidthSlider";
import { useT } from "../i18n";

export default function EmailView() {
  const t = useT();
  const { maxWidth, slider } = useViewWidth("viewWidth.email");
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({ ...DEFAULT_EMAIL_SETTINGS });
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(true);
  const [snapshot, setSnapshot] = useState<EmailSettings | null>(null);
  const [status, setStatus] = useState("");
  const [statusTone, setStatusTone] = useState("info");
  const notify = (text: string, tone = "success") => { setStatus(text); setStatusTone(tone); };
  const notifyError = (text: string) => { setStatus(text); setStatusTone("error"); };

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const sid = await ensureSpreadsheet();
        setSpreadsheetId(sid);
        setEmailSettings(await readEmailSettings(sid));
      } catch (e) {
        notifyError(`${t("common.error")}: ${(e as Error).message}`);
      } finally {
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k: keyof EmailSettings, v: string) => setEmailSettings((s) => ({ ...s, [k]: v }));

  function startEdit() {
    if (!window.confirm(t("email.confirmEdit"))) return;
    setSnapshot(emailSettings);
    setLocked(false);
  }

  function cancelEdit() {
    if (snapshot) setEmailSettings(snapshot);
    setSnapshot(null);
    setLocked(true);
  }

  async function handleSave() {
    if (!spreadsheetId || locked) return;
    if (!window.confirm(t("email.confirmSave"))) return;
    setBusy(true);
    try {
      await writeEmailSettings(spreadsheetId, emailSettings);
      notify(t("email.saved"));
      setSnapshot(null);
      setLocked(true);
    } catch (e) {
      notifyError(t("email.saveError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    "mt-1 w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed";

  return (
    <>
    {busy && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 select-none">
          <div className="relative">
            <img src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`} alt="MeW" className="w-16 h-16 opacity-90" />
            <span className="absolute inset-0 -m-2.5 rounded-full border-4 border-pink-200 border-t-pink-600 animate-spin" />
          </div>
          <p className="text-sm text-gray-500 tracking-wide">{t("common.processing")}</p>
        </div>
      </div>
    )}
    <div className="flex justify-end px-4 pt-2">{slider}</div>
    <div className="mx-auto p-4 space-y-4" style={{ maxWidth }}>
      <h2 className="font-bold text-xl flex items-center gap-2 text-gray-800">
        <i className="fa-solid fa-envelope text-pink-600" aria-hidden="true" />
        {t("email.title")}
      </h2>

      {locked && (
        <div className="text-sm rounded-md px-3 py-2 flex items-center gap-2 border text-amber-800 bg-amber-50 border-amber-100">
          <i className="fa-solid fa-lock mt-0.5" aria-hidden="true" />
          <span>{t("email.locked")}</span>
        </div>
      )}

      {status && (
        <div className={`text-sm rounded-md px-3 py-2 flex items-start gap-2 border ${
          statusTone === "error" ? "text-red-700 bg-red-50 border-red-100"
          : statusTone === "success" ? "text-green-700 bg-green-50 border-green-100"
          : "text-gray-700 bg-gray-50 border-gray-200"}`}>
          <i className={`fa-solid mt-0.5 ${statusTone === "error" ? "fa-circle-exclamation" : statusTone === "success" ? "fa-circle-check" : "fa-circle-info"}`} aria-hidden="true" />
          <span>{status}</span>
        </div>
      )}

      <div className="space-y-3">
        <label className="block text-sm text-gray-700">{t("email.dest")}
          <input type="text" value={emailSettings.dest} onChange={(e) => update("dest", e.target.value)} disabled={locked} className={inputCls} />
        </label>
        <div className="flex items-center gap-2">
          <label className="flex-1 text-sm text-gray-700">{t("email.cc")}
            <input type="text" value={emailSettings.cc} onChange={(e) => update("cc", e.target.value)} disabled={locked} className={inputCls} />
          </label>
          <label className="flex-1 text-sm text-gray-700">{t("email.bcc")}
            <input type="text" value={emailSettings.bcc} onChange={(e) => update("bcc", e.target.value)} disabled={locked} className={inputCls} />
          </label>
        </div>
        <label className="block text-sm text-gray-700">{t("email.senderName")}
          <input type="text" value={emailSettings.sendername} onChange={(e) => update("sendername", e.target.value)} disabled={locked} className={inputCls} />
        </label>
        <label className="block text-sm text-gray-700">{t("email.subject")}
          <input type="text" value={emailSettings.subject} onChange={(e) => update("subject", e.target.value)} disabled={locked} className={inputCls} />
        </label>
        <label className="block text-sm text-gray-700">{t("email.topMessage")}
          <PersistentTextarea storageKey="email-topmessage" value={emailSettings.topmessage} onChange={(e) => update("topmessage", e.target.value)} disabled={locked} rows={5} className={`${inputCls} resize-y`} />
        </label>
        <label className="block text-sm text-gray-700">{t("email.bottomMessage")}
          <PersistentTextarea storageKey="email-bottommessage" value={emailSettings.bottommessage} onChange={(e) => update("bottommessage", e.target.value)} disabled={locked} rows={3} className={`${inputCls} resize-y`} />
        </label>
        {locked ? (
          <button className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 text-white rounded-md px-3 py-2 hover:bg-indigo-700 disabled:opacity-50 transition-colors" onClick={startEdit} disabled={busy || !spreadsheetId}>
            <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
            <span>{t("email.edit")}</span>
          </button>
        ) : (
          <div className="flex gap-2">
            <button className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 text-white rounded-md px-3 py-2 hover:bg-emerald-700 disabled:opacity-50 transition-colors" onClick={handleSave} disabled={busy || !spreadsheetId}>
              <i className="fa-solid fa-floppy-disk" aria-hidden="true" />
              <span>{t("email.save")}</span>
            </button>
            <button className="inline-flex items-center justify-center gap-2 bg-gray-200 text-gray-700 rounded-md px-3 py-2 hover:bg-gray-300 disabled:opacity-50 transition-colors" onClick={cancelEdit} disabled={busy}>
              <i className="fa-solid fa-xmark" aria-hidden="true" />
              <span>{t("email.cancel")}</span>
            </button>
          </div>
        )}
        {spreadsheetId && (
          <div className="text-xs text-gray-600 pt-2">
            <a href={spreadsheetUrl(spreadsheetId)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-700 hover:underline">
              <i className="fa-solid fa-up-right-from-square" aria-hidden="true" />
              <span>{t("sheet.openGoogleSheet")}</span>
            </a>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
